import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import BreadcrumbNew from "../../BreadcrumbNew";
import Button from "../../Button";
import TextLink from "../../TextLink";
import VerticalMenu from "../../VerticalMenu";
import AccessibilityHeader from "../AccessibilityHeader";
import { externalLinks } from "../AccessibilityHeader/externalLinks";

import logoPgmWhite from "../../../assets/images/landingPage/logo-pgm-white-mode.svg";
import logoMosaic from "../../../assets/images/landingPage/LogoMosaic.svg";
import brasaoPgm from "../../../assets/images/landingPage/brasao-pgm.png";

import style from "./style.module.scss";

interface NavigationMenuItem {
  labelKey: string;
  href: string;
  icon?: string;
  target?: "_blank";
}

const navigationButtonProperties = {
  shape: "round",
  size: "small",
  variant: "text",
} as const;

const keyI18n = "translations.navbar";

const EXPANDED_BREAKPOINT = 1200;
const SWIPE_CLOSE_THRESHOLD = 60;

const homeItem: NavigationMenuItem = {
  labelKey: `${keyI18n}.home`,
  href: "/",
  icon: "home",
};

const serviceMenuItems: NavigationMenuItem[] = [
  {
    labelKey: `${keyI18n}.services.debts`,
    href: "/consultar/debitos/em_aberto",
    icon: "mintmark",
  },
  {
    labelKey: `${keyI18n}.services.installment`,
    href: "/consultar/parcelamentos",
    icon: "checkbook",
  },
  {
    labelKey: `${keyI18n}.services.protested`,
    href: "/consultar/protestos",
    icon: "currency_exchange",
  },
  {
    labelKey: `${keyI18n}.services.signature`,
    href: "/validar-documento",
    icon: "signature",
  },
  {
    labelKey: `${keyI18n}.services.library`,
    href: "https://signon.thomsonreuters.com/?productid=EREAD&viewproductid=EREAD&returnto=https%3A%2F%2Fproview.thomsonreuters.com%2Flogin.html&culture=pt-BR&lr=0&bhcp=1",
    icon: "local_library",
    target: "_blank",
  },
  {
    labelKey: `${keyI18n}.services.seeAll`,
    href: "/servicos",
    icon: "info",
  },
];

const institutionalMenuItems: NavigationMenuItem[] = [
  {
    labelKey: `${keyI18n}.institutional.pgm`,
    href: "/apgm",
    icon: "corporate_fare",
  },
  {
    labelKey: `${keyI18n}.institutional.whoswho`,
    href: "/quem_e_quem",
    icon: "groups_2",
  },
  {
    labelKey: `${keyI18n}.institutional.regulatoryLaws`,
    href: "/leis_regulamentadoras",
    icon: "balance",
  },
  {
    labelKey: `${keyI18n}.institutional.news`,
    href: "/noticias",
    icon: "newspaper",
  },
  {
    labelKey: `${keyI18n}.institutional.ethics`,
    href: "/Manual_Codigo_de_Etica.pdf",
    icon: "diversity_3",
    target: "_blank",
  },
  {
    labelKey: `${keyI18n}.institutional.strategicPlanning`,
    href: "/Planejamento_estratégico.pdf",
    icon: "event_note",
    target: "_blank",
  },
  {
    labelKey: `${keyI18n}.institutional.transparency`,
    href: "/transparencia",
    icon: "table_eye",
  },
];

const bottomMenuItems: NavigationMenuItem[] = [
  { labelKey: `${keyI18n}.doubts`, href: "/duvidas", icon: "contact_support" },
  { labelKey: `${keyI18n}.contacts`, href: "/contatos", icon: "contact_page" },
];

export default function NewNavbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const modalOpen = navigationOpen || linksOpen;

  const closeAll = () => {
    setNavigationOpen(false);
    setLinksOpen(false);
  };

  const isActive = (href: string) => {
    if (href === "/") {
      return pathname === "/";
    }

    return pathname === href || pathname.startsWith(`${href}/`);
  };

  const renderMenuItems = (items: NavigationMenuItem[]) =>
    items.map(({ href, icon, labelKey, target }) => (
      <VerticalMenu.Item
        key={labelKey}
        href={href}
        target={target}
        icon={
          icon ? (
            <span className="material-symbols-sharp">{icon}</span>
          ) : undefined
        }
      >
        {t(labelKey)}
      </VerticalMenu.Item>
    ));

  const renderDrawerItem = ({ href, icon, labelKey, target }: NavigationMenuItem) => {
    const className = [
      style.drawerItem,
      isActive(href) ? style.drawerItemActive : "",
    ]
      .filter(Boolean)
      .join(" ");

    const content = (
      <>
        {icon ? (
          <span aria-hidden="true" className={`material-symbols-sharp ${style.drawerItemIcon}`}>
            {icon}
          </span>
        ) : null}
        <span className={`pgm-body-medium ${style.drawerItemLabel}`}>{t(labelKey)}</span>
      </>
    );

    if (target === "_blank" || href.startsWith("http")) {
      return (
        <a key={labelKey} className={className} href={href} target={target} rel="noopener noreferrer">
          {content}
        </a>
      );
    }

    return (
      <Link key={labelKey} className={className} to={href}>
        {content}
      </Link>
    );
  };

  useEffect(() => {
    closeAll();
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= EXPANDED_BREAKPOINT) {
        closeAll();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAll();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (touchStartX.current === null) {
      return;
    }

    const deltaX = event.touches[0].clientX - touchStartX.current;

    if (deltaX < -SWIPE_CLOSE_THRESHOLD) {
      setNavigationOpen(false);
      touchStartX.current = null;
    }
  };

  const handleTouchEnd = () => {
    touchStartX.current = null;
  };

  return (
    <>
      <AccessibilityHeader />
      <nav className={style.newNavbar}>
        <div className={style.newNavbarContainer}>
          <Button
            {...navigationButtonProperties}
            iconButton
            className={style.newNavbarToggler}
            aria-controls="newnavbar-navigation"
            aria-expanded={navigationOpen}
            aria-label={t(`${keyI18n}.aria.openNavigation`)}
            onClick={() => setNavigationOpen(true)}
            icon={
              <span className={`material-symbols-sharp ${style.newNavbarMenuIcon}`}>
                menu
              </span>
            }
          />
          <div className={style.logoContainer}>
            <img className={style.logoMosaic} src={logoMosaic} alt="" />
            <Link
              aria-label={t(`${keyI18n}.aria.homeLink`)}
              className={`${style.logoLink} ${style.logoFull}`}
              to="/"
            >
              <img
                src={logoPgmWhite}
                alt={t(`${keyI18n}.aria.pgmLogo`)}
                height={44}
              />
            </Link>
            <Link
              aria-label={t(`${keyI18n}.aria.homeLink`)}
              className={`${style.logoLink} ${style.logoCompact}`}
              to="/"
            >
              <img src={brasaoPgm} alt={t(`${keyI18n}.aria.pgmLogo`)} height={40} />
            </Link>
          </div>
          <div className={style.newNavbarNavigation}>
            <ul className={style.navigationList}>
              <li>
                <Button {...navigationButtonProperties} href="/">
                  {t(`${keyI18n}.home`)}
                </Button>
              </li>
              <li>
                <div className={style.navigationDropdown}>
                  <Button
                    {...navigationButtonProperties}
                    id="servicos-link"
                    aria-haspopup="menu"
                    aria-controls="servicos-menu"
                  >
                    {t(`${keyI18n}.services.menu`)}
                  </Button>
                  <VerticalMenu
                    id="servicos-menu"
                    anchor="servicos-link"
                    className={style.servicesMenu}
                    interaction="hover"
                    yOffset={4}
                    aria-label={t(`${keyI18n}.aria.servicesMenu`)}
                  >
                    {renderMenuItems(serviceMenuItems)}
                  </VerticalMenu>
                </div>
              </li>
              <li>
                <div className={style.navigationDropdown}>
                  <Button
                    {...navigationButtonProperties}
                    id="institucional-link"
                    aria-haspopup="menu"
                    aria-controls="institucional-menu"
                  >
                    {t(`${keyI18n}.institutional.menu`)}
                  </Button>
                  <VerticalMenu
                    id="institucional-menu"
                    anchor="institucional-link"
                    className={style.institutionalMenu}
                    interaction="hover"
                    yOffset={4}
                    aria-label={t(`${keyI18n}.aria.institutionalMenu`)}
                  >
                    {renderMenuItems(institutionalMenuItems)}
                  </VerticalMenu>
                </div>
              </li>
              <li>
                <Button {...navigationButtonProperties} href="/duvidas">
                  {t(`${keyI18n}.doubts`)}
                </Button>
              </li>
              <li>
                <Button {...navigationButtonProperties} href="/contatos">
                  {t(`${keyI18n}.contacts`)}
                </Button>
              </li>
              <li>
                <Button
                  {...navigationButtonProperties}
                  iconButton
                  href="/login"
                  aria-label={t(`${keyI18n}.tooltip.attendant`)}
                  tooltip={t(`${keyI18n}.tooltip.attendant`)}
                  tooltipPlacement="bottom-end"
                  icon={
                    <span className={`material-symbols-sharp ${style.newNavbarAttendantIcon}`}>
                      admin_panel_settings
                    </span>
                  }
                />
              </li>
            </ul>
          </div>
          <div className={style.newNavbarTrailing}>
            <Button
              {...navigationButtonProperties}
              iconButton
              className={style.newNavbarTrailingButton}
              aria-label={t(`${keyI18n}.aria.externalLinks`)}
              aria-expanded={linksOpen}
              onClick={() => setLinksOpen(true)}
              icon={
                <span className={`material-symbols-sharp ${style.newNavbarMenuIcon}`}>
                  open_in_new
                </span>
              }
            />
          </div>
        </div>

        <div
          aria-hidden="true"
          className={[style.drawerScrim, navigationOpen ? style.scrimOpen : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setNavigationOpen(false)}
        />

        <aside
          className={[style.drawer, navigationOpen ? style.drawerOpen : ""]
            .filter(Boolean)
            .join(" ")}
          id="newnavbar-navigation"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Button
            {...navigationButtonProperties}
            iconButton
            className={style.drawerClose}
            aria-label={t(`${keyI18n}.aria.closeNavigation`)}
            onClick={() => setNavigationOpen(false)}
            icon={
              <span className={`material-symbols-sharp ${style.newNavbarMenuIcon}`}>
                menu_open
              </span>
            }
          />
          <div className={style.drawerScroll}>
            <nav className={style.drawerNav} aria-label={t(`${keyI18n}.aria.openNavigation`)}>
              {renderDrawerItem(homeItem)}

              <p className={`pgm-title-small ${style.drawerSection}`}>
                {t(`${keyI18n}.services.menu`)}
              </p>
              {serviceMenuItems.map(renderDrawerItem)}

              <p className={`pgm-title-small ${style.drawerSection}`}>
                {t(`${keyI18n}.institutional.menu`)}
              </p>
              {institutionalMenuItems.map(renderDrawerItem)}

              <hr className={style.drawerDivider} />

              {bottomMenuItems.map(renderDrawerItem)}
            </nav>
          </div>

          <div className={style.drawerFooter}>
            <Button
              href="/login"
              variant="outlined"
              shape="round"
              size="small"
              fullWidth
              icon={
                <span className="material-symbols-sharp">admin_panel_settings</span>
              }
            >
              {t(`${keyI18n}.tooltip.attendant`)}
            </Button>
          </div>
        </aside>

        <div
          aria-hidden="true"
          className={[style.sheetScrim, linksOpen ? style.scrimOpen : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setLinksOpen(false)}
        />

        <div
          className={[style.sheet, linksOpen ? style.sheetOpen : ""]
            .filter(Boolean)
            .join(" ")}
          role="dialog"
          aria-modal="true"
          aria-label={t(`${keyI18n}.externalLinksTitle`)}
        >
          <span aria-hidden="true" className={style.sheetHandle} />
          <h2 className={`pgm-title-large ${style.sheetTitle}`}>
            {t(`${keyI18n}.externalLinksTitle`)}
          </h2>
          <div className={style.sheetLinks}>
            {externalLinks.map(({ href, labelKey }) => (
              <TextLink
                className={`pgm-body-large ${style.sheetLink}`}
                key={href}
                link={href}
                target="_blank"
              >
                <span className={style.sheetLinkText}>{t(labelKey)}</span>
                <span aria-hidden="true" className={`material-symbols-sharp ${style.sheetLinkIcon}`}>
                  open_in_new
                </span>
              </TextLink>
            ))}
          </div>
        </div>

        <div className={style.decoratorLine}>
          <div className={style.line1} />
          <div className={style.line2} />
          <div className={style.line3Container}>
            <div className={style.line1} />
            <div className={style.line3} />
          </div>
        </div>
      </nav>
      <BreadcrumbNew />
    </>
  );
}
